/**
 * @fileoverview Shared "Field source" legend strip.
 *
 * Renders a row of colour-coded chips that document where each placeholder's
 * value comes from (Job, Org, Custom, Template, Manual, Job Contact, Firm
 * Contact). The chip colours come from the same `data-field-source` CSS
 * declarations in `src/index.css` that the letter Preview and the field
 * insertion picker use, so the legend stays in lock-step with everything
 * else without per-call-site duplication.
 *
 * Used by:
 *  - `PreviewDialog` (Preview tab) — explains the chips inlined in the body.
 *  - `PdfFieldEditor` (Map Fields tab) — explains the rectangles drawn on
 *    the PDF and the chips in the right-hand Fields panel (MOR-462).
 */

import { FIELD_SOURCE_STYLES } from "@/utils/precedents/classifyFieldSource";
import { cn } from "@/utils/misc";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/ui/composed/overlays/tooltip";
import { SOURCE_LEGEND } from "./SourceLegend.helpers";

export function SourceLegend({ className }: { className?: string }) {
  return (
    <TooltipProvider delayDuration={200}>
      <div
        className={cn(
          "flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-muted-foreground",
          className,
        )}
      >
        <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70">
          Field source
        </span>
        {SOURCE_LEGEND.map(({ source, label }) => (
          <Tooltip key={source}>
            <TooltipTrigger asChild>
              <span
                className="field-placeholder-chip inline-flex items-center"
                data-field-source={source}
              >
                {label}
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-56 text-xs">
              {FIELD_SOURCE_STYLES[source].help}
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
    </TooltipProvider>
  );
}
